import React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { 
  Dialog, 
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Gamepad, Medal, Trophy, Star, UserCheck, Clock, UserPlus } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface PlayerProfileDialogProps {
  playerId: number | null;
  playerUsername?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PlayerProfileData {
  user: {
    id: number;
    username: string;
    createdAt: string;
  };
  profile?: {
    avatarId: number | null;
    avatarSource: string | null;
    bannerId: number | null;
    bannerSource: string | null;
    status: string;
  };
  stats: {
    gamesPlayed: number;
    gamesWon: number;
    winRate: number;
    highestScore: number;
  };
}

export function PlayerProfileDialog({
  playerId,
  playerUsername,
  isOpen,
  onOpenChange
}: Readonly<PlayerProfileDialogProps>) {
  const [_location, _navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const isOwnProfile = user?.id === playerId;

  // Fetch player profile data
  const { data: profileData, isLoading } = useQuery<PlayerProfileData>({
    queryKey: ["player-profile-preview", playerId],
    queryFn: async () => {
      if (!playerId) return null;
      try {
        const res = await apiRequest("GET", `/api/profile/${playerId}/preview`);
        if (!res.ok) throw new Error("Failed to fetch profile data");
        return res.json();
      } catch (error) {
        console.error(`Error fetching profile for player ${playerId}:`, error);
        throw error;
      }
    },
    enabled: isOpen && !!playerId,
    staleTime: 1 * 60 * 1000, // Cache for 1 minute
  });

  const { data: avatarData } = useQuery({
    queryKey: ["avatar-data", profileData?.profile?.avatarId, profileData?.profile?.avatarSource],
    queryFn: async () => {
      if (!profileData?.profile?.avatarId) return null;
      
      // Construct endpoint based on avatarSource
      const source = profileData.profile.avatarSource || "default";
      const endpoint = source === "shop" 
        ? `/api/customization/shopItem/${profileData.profile.avatarId}` 
        : `/api/customization/avatars/${profileData.profile.avatarId}`;
        
      const res = await apiRequest("GET", endpoint);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!profileData?.profile?.avatarId,
  });



  // Fetch banner data if bannerId is present
  const { data: bannerData } = useQuery({
    queryKey: ["banner-data", profileData?.profile?.bannerId, profileData?.profile?.bannerSource],
    queryFn: async () => {
      if (!profileData?.profile?.bannerId) return null;

      const endpoint = `/api/customization/banners/${profileData.profile.bannerId}`;

      const res = await apiRequest("GET", endpoint);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!profileData?.profile?.bannerId,
  });

  // Check if users are already friends
  const { data: friendsList } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/friends");
      return res.json();
    },
    enabled: isOpen && !!user && !isOwnProfile,
  });

  // Determine if the profile user is already a friend
  const isFriend = friendsList?.some(
    (friend: any) =>
      (friend.userId === playerId || friend.friendId === playerId) &&
      friend.status === "accepted",
  );

  // Send friend request mutation
  const sendFriendRequest = useMutation({
    mutationFn: async (friendId: number) => {
      const res = await apiRequest("POST", "/api/friends/request", { friendId });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to send friend request");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Friend request sent",
        description: `A request has been sent to ${profileData?.user.username}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send request",
        description: error.message || "Could not send friend request",
        variant: "destructive",
      });
    },
  });

  const handleSendFriendRequest = () => {
    if (playerId) {
      sendFriendRequest.mutate(playerId);
    }
  };

  const initials = playerUsername
    ? playerUsername
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "??";

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black/95 border border-primary/30 shadow-lg shadow-primary/5 max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-xl text-white flex items-center gap-2">
            Player Profile
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-400">Loading profile...</p>
          </div>
        ) : !profileData ? (
          <div className="py-8 text-center">
            <p className="text-gray-400">Player profile not found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Level Badge */}
            <div className="flex items-center justify-between">
             
              
            </div>

            {/* Profile Section */}
            <div>
              {/* Avatar + Info */}
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 ring-2 ring-primary/30">
                  {avatarData?.imageUrl ? (
                    <AvatarImage src={avatarData.imageUrl} alt={profileData.user.username} />
                  ) : (
                    <AvatarFallback className="bg-primary/20 text-white font-medium">
                      {initials}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center">
                    {profileData.user.username}
                    {profileData.profile?.status === "premium" && (
                      <span className="ml-1.5 text-yellow-400">
                        <svg 
                          viewBox="0 0 24 24" 
                          fill="currentColor" 
                          className="h-4 w-4"
                        >
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                        </svg>
                      </span>
                    )}
                    <Badge variant="outline" className="bg-black/60 text-primary border-primary/20 px-2 py-1 h-6">
                  Level {profileData.stats?.level || 1}
                </Badge>
                    {isFriend && (
                      <Badge variant="outline" className="ml-2 border-primary/30 text-primary bg-primary/10">
                        <UserCheck className="h-3 w-3 mr-1" />
                        Friends
                      </Badge>
                    )}
                  </h3>
                  <div className="flex items-center mt-1 text-sm text-gray-400">
                    <Clock className="h-3.5 w-3.5 mr-1.5" />
                    Joined {new Date(profileData.user.createdAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                    })}
                  </div>
                </div>
              </div>

              
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 gap-3">
              <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                <div className="p-4">
                  <div className="flex flex-col items-center text-center">
                    <Gamepad className="h-8 w-8 text-primary mb-2" />
                    <div className="text-2xl font-bold mb-0.5">{profileData.stats.gamesPlayed || 0}</div>
                    <div className="text-xs text-white/60">Matches Played</div>
                  </div>
                </div>
              </Card>

              <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                <div className="p-4">
                  <div className="flex flex-col items-center text-center">
                    <Trophy className="h-8 w-8 text-green-500 mb-2" />
                    <div className="text-2xl font-bold mb-0.5">{profileData.stats.winRate || 0}%</div>
                    <div className="text-xs text-white/60">Win Rate</div>
                  </div>
                </div>
              </Card>

              <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                <div className="p-4">
                  <div className="flex flex-col items-center text-center">
                    <Medal className="h-8 w-8 text-yellow-500 mb-2" />
                    <div className="text-2xl font-bold mb-0.5">{profileData.stats.gamesWon || 0}</div>
                    <div className="text-xs text-white/60">Matches Won</div>
                  </div>
                </div>
              </Card>

              <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                <div className="p-4">
                  <div className="flex flex-col items-center text-center">
                    <Star className="h-8 w-8 text-blue-500 mb-2" />
                    <div className="text-2xl font-bold mb-0.5">{profileData.stats.highestScore || 0}</div>
                    <div className="text-xs text-white/60">Highest Score</div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Action Button - only show if not own profile and not already friends */}
            {!isOwnProfile && !isFriend && (
              <div className="pt-2">
                <Button 
                  className="w-full bg-gradient-to-r from-primary/90 to-primary shadow-lg shadow-primary/20 border border-primary/20 text-black font-medium"
                  onClick={handleSendFriendRequest}
                  disabled={sendFriendRequest.isPending}
                >
                  {sendFriendRequest.isPending ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent mr-2"></div>
                      Sending...
                    </div>
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add Friend
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}